import { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import type { ReactNode } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { friendlyError } from '@/lib/errors';
import { logActivity } from '@/lib/activityLog';

export interface Profile {
  id: string;
  role: string;
  first_name: string | null;
  last_name: string | null;
  phone: string | null;
  avatar_url: string | null;
  username: string | null;
  email: string | null;
  branch_id: string | null;
  blocked: boolean;
}

interface AuthContextValue {
  user: User | null;
  profile: Profile | null;
  /** true solo mientras se resuelve la sesión inicial. Nunca vuelve a true. */
  loading: boolean;
  /** true cuando el perfil del usuario ya se intentó cargar (éxito o error). */
  profileReady: boolean;
  error: string | null;
  signIn: (email: string, password: string) => Promise<{ error: string | null }>;
  signOut: () => Promise<void>;
  isAdmin: boolean;
  retry: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const ADMIN_ROLES = ['super_admin', 'administrador'];

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [sessionLoading, setSessionLoading] = useState(true);
  const [profileReady, setProfileReady] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Deduplicación de la carga de perfil: evita ráfagas de consultas repetidas
  // cuando Supabase emite varios eventos de autenticación seguidos.
  const inflightRef = useRef<{ userId: string; promise: Promise<Profile | null> } | null>(null);
  const loadedUserIdRef = useRef<string | null>(null);

  /**
   * Carga el perfil. Nunca rechaza: los errores se guardan en el estado.
   * Con `silent = true` no activa el indicador global de carga, para que un
   * refresco de token no desmonte la pantalla que el usuario está usando.
   */
  const fetchProfile = useCallback((userId: string, silent = false): Promise<Profile | null> => {
    if (inflightRef.current?.userId === userId) return inflightRef.current.promise;

    const promise = (async (): Promise<Profile | null> => {
      try {
        const { data, error: profileError } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', userId)
          .maybeSingle();

        if (profileError) throw profileError;

        const p = (data as Profile) ?? null;
        setProfile(p);
        loadedUserIdRef.current = userId;
        return p;
      } catch (err) {
        setError(friendlyError(err, 'No se pudo cargar tu perfil'));
        return null;
      } finally {
        setProfileReady(true);
        if (inflightRef.current?.userId === userId) inflightRef.current = null;
      }
    })();

    inflightRef.current = { userId, promise };
    return promise;
  }, []);

  const loadSession = useCallback(async () => {
    setError(null);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();

      setUser(session?.user ?? null);
      // La sesión ya está resuelta: la interfaz puede pintarse sin esperar el
      // perfil. Así el POS no se desmonta en cada refresco de token.
      setSessionLoading(false);

      if (session?.user) {
        void fetchProfile(session.user.id);
      } else {
        setProfile(null);
        loadedUserIdRef.current = null;
        setProfileReady(true);
      }
    } catch (err) {
      const rawMessage = err instanceof Error ? err.message : String(err);
      const isStaleSession = /invalid refresh token|refresh token not found|refresh token.*expired|session not found/i.test(
        rawMessage
      );

      if (isStaleSession) {
        // La sesión guardada ya no es válida: limpiamos el estado local sin
        // molestar al usuario ni llamar al servidor (que también fallaría).
        setUser(null);
        setProfile(null);
        setProfileReady(true);
        setError(null);
        setSessionLoading(false);
        try {
          await supabase.auth.signOut({ scope: 'local' });
        } catch {
          /* ya era inválida, no hay nada que revocar */
        }
      } else {
        setUser(null);
        setProfile(null);
        setProfileReady(true);
        setError(friendlyError(err, 'No se pudo cargar la sesión'));
        setSessionLoading(false);
      }
    }
  }, [fetchProfile]);

  useEffect(() => {
    void loadSession();

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      // El token se refresca cada ~55 minutos. Ese evento NO debe recargar el
      // perfil ni volver a mostrar la pantalla de carga: hacerlo desmontaba el
      // POS a media venta y se percibía como un congelamiento.
      if (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') {
        if (session?.user) setUser(session.user);
        return;
      }

      if (event === 'INITIAL_SESSION') {
        // La sesión inicial ya la resolvió loadSession().
        return;
      }

      const nextUser = session?.user ?? null;
      setUser(nextUser);
      setSessionLoading(false);

      if (!nextUser) {
        setProfile(null);
        loadedUserIdRef.current = null;
        setProfileReady(true);
        return;
      }

      // Si el perfil de este usuario ya está cargado, no lo pedimos otra vez.
      if (loadedUserIdRef.current === nextUser.id) {
        setProfileReady(true);
        return;
      }

      void fetchProfile(nextUser.id);
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [fetchProfile, loadSession]);

  const signIn = useCallback(
    async (email: string, password: string) => {
      setError(null);
      const normalizedEmail = email.trim().toLowerCase();

      // Protección contra fuerza bruta: verificamos si el correo está bloqueado.
      try {
        const { data: lock } = await supabase.rpc('login_lock_status', {
          p_email: normalizedEmail,
        });
        const lockInfo = lock as { locked?: boolean; retry_after_seconds?: number } | null;
        if (lockInfo?.locked) {
          const minutes = Math.max(1, Math.ceil((lockInfo.retry_after_seconds ?? 0) / 60));
          return {
            error: `Demasiados intentos fallidos. Espera ${minutes} minuto(s) e inténtalo de nuevo.`,
          };
        }
      } catch {
        // Si la verificación falla, no bloqueamos a un usuario legítimo.
      }

      const { data, error: signInError } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (signInError) {
        // Registramos el intento fallido sin revelar si el correo existe.
        try {
          await supabase.rpc('register_login_attempt', {
            p_email: normalizedEmail,
            p_success: false,
          });
        } catch {
          /* el registro no debe bloquear el flujo */
        }
        void logActivity({
          module: 'auth',
          action: 'login_failed',
          level: 'WARNING',
          result: 'failure',
          description: 'Intento de inicio de sesión fallido',
          targetType: 'email',
          targetId: normalizedEmail,
        });
        return { error: signInError.message };
      }

      if (data.user) {
        const p = await fetchProfile(data.user.id);
        if (p?.blocked) {
          await supabase.auth.signOut();
          setProfile(null);
          setUser(null);
          loadedUserIdRef.current = null;
          void logActivity({
            module: 'auth',
            action: 'login_blocked',
            level: 'WARNING',
            result: 'failure',
            description: 'Intento de acceso con una cuenta desactivada',
            targetType: 'user',
            targetId: data.user.id,
          });
          return { error: 'Tu cuenta ha sido desactivada. Contacta al administrador.' };
        }
      }

      // Inicio exitoso: limpiamos intentos fallidos y lo auditamos.
      try {
        await supabase.rpc('register_login_attempt', {
          p_email: normalizedEmail,
          p_success: true,
        });
      } catch {
        /* ignore */
      }
      void logActivity({
        module: 'auth',
        action: 'login_success',
        level: 'INFO',
        result: 'success',
        description: 'Inicio de sesión exitoso',
        targetType: 'user',
        targetId: data.user?.id,
      });

      return { error: null };
    },
    [fetchProfile]
  );

  const signOut = useCallback(async () => {
    // Registramos el cierre de sesión antes de cerrarla (para conservar el usuario).
    try {
      await logActivity({
        module: 'auth',
        action: 'logout',
        level: 'INFO',
        result: 'success',
        description: 'Cierre de sesión',
        targetType: 'user',
        targetId: user?.id,
      });
    } catch {
      /* ignore */
    }
    await supabase.auth.signOut();
    setProfile(null);
    setUser(null);
    loadedUserIdRef.current = null;
    setProfileReady(true);
  }, [user?.id]);

  const isAdmin = useMemo(() => {
    return profile ? ADMIN_ROLES.includes(profile.role) : false;
  }, [profile]);

  const value = useMemo(
    () => ({
      user,
      profile,
      loading: sessionLoading,
      profileReady,
      error,
      signIn,
      signOut,
      isAdmin,
      retry: () => {
        setSessionLoading(true);
        void loadSession();
      },
    }),
    [user, profile, sessionLoading, profileReady, error, signIn, signOut, isAdmin, loadSession]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth debe usarse dentro de un AuthProvider');
  }
  return ctx;
}